import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ChevronDown, Filter } from "lucide-react";
import { useAuctions } from "../../contexts/AuctionsContext";
import { BidAuctionCard } from "../../components/auction/BidAuctionCard";
import {
  CheckboxGroup,
  EmptyState,
  FilterSection,
  RangeFilterWithSlider,
} from "../../components/auction/FilterSection";
import { FilterDrawer } from "../../components/auction/FilterDrawer";
import type { PublicAuction } from "../../lib/types";
import { normalizeFuelType, normalizeTransmission, normalizeWheelDrive } from "../../lib/auction-utils";
import { usePlatformAuth } from "../../lib/use-platform-auth";
import { useFavoriteAuctions } from "../../lib/use-favorite-auctions";

type SortKey = "ends_soon" | "price_asc" | "price_desc" | "newest";

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "ends_soon", label: "Avsluttes snart" },
  { value: "price_asc", label: "Laveste pris" },
  { value: "price_desc", label: "Høyeste pris" },
  { value: "newest", label: "Nyeste" },
];

function sortAuctions(auctions: PublicAuction[], sort: SortKey): PublicAuction[] {
  return [...auctions].sort((a, b) => {
    switch (sort) {
      case "ends_soon":
        if (!a.endsAt) return 1;
        if (!b.endsAt) return -1;
        return new Date(a.endsAt).getTime() - new Date(b.endsAt).getTime();
      case "price_asc":
        return (a.currentBid || a.startingBid) - (b.currentBid || b.startingBid);
      case "price_desc":
        return (b.currentBid || b.startingBid) - (a.currentBid || a.startingBid);
      case "newest":
        return 0; // already sorted by server
    }
  });
}

export function BudrunderPage() {
  const { organizationSlug } = useParams<{ organizationSlug: string }>();
  const { organizations } = usePlatformAuth();
  const { auctions, loading: auctionsLoading, error: auctionsError, applyAuctionEvent } = useAuctions();

  const [pendingPurchases, setPendingPurchases] = useState<PublicAuction[]>([]);
  const [pendingLoading, setPendingLoading] = useState(true);

  const currentOrg = useMemo(
    () => organizations.find((o) => o.slug === organizationSlug),
    [organizations, organizationSlug]
  );

  const [yearFrom, setYearFrom] = useState("");
  const [yearTo, setYearTo] = useState("");
  const [kmFrom, setKmFrom] = useState("");
  const [kmTo, setKmTo] = useState("");
  const [fuels, setFuels] = useState<string[]>([]);
  const [transmissions, setTransmissions] = useState<string[]>([]);
  const [wheelDrives, setWheelDrives] = useState<string[]>([]);
  const [bodyTypes, setBodyTypes] = useState<string[]>([]);
  const [makes, setMakes] = useState<string[]>([]);
  const [sort, setSort] = useState<SortKey>("ends_soon");
  const [sortOpen, setSortOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const { favorites, toggleFavorite } = useFavoriteAuctions();

  useEffect(() => {
    if (!organizationSlug) return;
    const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
    setPendingLoading(true);
    fetch(`${api}/api/organizations/${organizationSlug}/purchases`, { credentials: "include" })
      .then((res) => {
        if (!res.ok) throw new Error("Kunne ikke laste pendings");
        return res.json() as Promise<any[]>;
      })
      .then((data) => {
        // Map the purchases from the dedicated endpoint to PublicAuction shape
        const mapped = data
          .filter((p: any) => p.status === "pending_seller_decision" || p.status === "negotiating")
          .map(p => ({
            ...p,
            currentBid: p.finalPrice,
            // Add other required fields with defaults if missing
            bidCount: 0,
            startingBid: 0,
            minimumIncrement: 0,
            visibility: "public",
            lotNumber: null,
            description: "",
            referenceNumber: "",
            sellerOrganizationId: "",
            startsAt: null,
            endsAt: p.closedAt,
            createdAt: p.createdAt || p.closedAt,
          } as PublicAuction));
        setPendingPurchases(mapped);
      })
      .catch(() => setPendingPurchases([]))
      .finally(() => setPendingLoading(false));
  }, [organizationSlug]);

  const liveAuctions = useMemo(() => auctions.filter((a) => a.status === "live"), [auctions]);

  const pendingAuctions = useMemo(() => {
    // Merge results from both sources to be sure
    const contextPendings = currentOrg
      ? auctions.filter((a) =>
          (a.status === "pending_seller_decision" || a.status === "negotiating") &&
          a.highestBidderOrganizationId === currentOrg.id
        )
      : [];
    
    // Use an ID map to deduplicate
    const map = new Map<string, PublicAuction>();
    contextPendings.forEach(a => map.set(a.id, a));
    pendingPurchases.forEach(a => {
      if (!map.has(a.id)) map.set(a.id, a);
    });
    
    return Array.from(map.values());
  }, [auctions, currentOrg, pendingPurchases]);

  const makeOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of liveAuctions) {
      const make = a.vehicle.make;
      if (make) counts.set(make, (counts.get(make) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([label, count]) => ({ value: label, label, count }));
  }, [liveAuctions]);

  const fuelOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of liveAuctions) {
      const label = normalizeFuelType(a.vehicle.fuelType);
      if (label !== "–") counts.set(label, (counts.get(label) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([label, count]) => ({ value: label, label, count }));
  }, [liveAuctions]);

  const transmissionOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of liveAuctions) {
      const label = normalizeTransmission(a.vehicle.transmission);
      if (label !== "–") counts.set(label, (counts.get(label) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([label, count]) => ({ value: label, label, count }));
  }, [liveAuctions]);

  const wheelDriveOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of liveAuctions) {
      const label = normalizeWheelDrive(a.vehicle.wheelDrive);
      if (label !== "–") counts.set(label, (counts.get(label) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([label, count]) => ({ value: label, label, count }));
  }, [liveAuctions]);

  const bodyTypeOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of liveAuctions) {
      const bt = a.vehicle.bodyType;
      if (bt) counts.set(bt, (counts.get(bt) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([label, count]) => ({ value: label, label, count }));
  }, [liveAuctions]);

  const filtered = useMemo(() => {
    const result = liveAuctions.filter((a) => {
      const v = a.vehicle;
      if (yearFrom && v.modelYear !== null && v.modelYear < Number(yearFrom)) return false;
      if (yearTo && v.modelYear !== null && v.modelYear > Number(yearTo)) return false;
      if (kmFrom && v.mileage !== null && v.mileage < Number(kmFrom)) return false;
      if (kmTo && v.mileage !== null && v.mileage > Number(kmTo)) return false;
      if (fuels.length > 0 && !fuels.includes(normalizeFuelType(v.fuelType))) return false;
      if (transmissions.length > 0 && !transmissions.includes(normalizeTransmission(v.transmission))) return false;
      if (wheelDrives.length > 0 && !wheelDrives.includes(normalizeWheelDrive(v.wheelDrive))) return false;
      if (bodyTypes.length > 0 && (!v.bodyType || !bodyTypes.includes(v.bodyType))) return false;
      if (makes.length > 0 && !makes.includes(v.make)) return false;
      return true;
    });
    return sortAuctions(result, sort);
  }, [liveAuctions, yearFrom, yearTo, kmFrom, kmTo, fuels, transmissions, wheelDrives, bodyTypes, makes, sort]);

  function clearFilters() {
    setYearFrom("");
    setYearTo("");
    setKmFrom("");
    setKmTo("");
    setFuels([]);
    setTransmissions([]);
    setWheelDrives([]);
    setBodyTypes([]);
    setMakes([]);
  }

  const currentSortLabel = SORT_OPTIONS.find((o) => o.value === sort)?.label ?? "";

  const filterContent = (
    <>
      <FilterSection title="Årsmodell">
        <RangeFilterWithSlider
          fromValue={yearFrom} toValue={yearTo}
          fromPlaceholder="Fra" toPlaceholder="Til"
          min={2000} max={new Date().getFullYear()}
          onChange={(from, to) => { setYearFrom(from); setYearTo(to); }}
        />
      </FilterSection>

      <FilterSection title="Kilometerstand">
        <RangeFilterWithSlider
          fromValue={kmFrom} toValue={kmTo}
          fromPlaceholder="Fra km" toPlaceholder="Til km"
          min={0} max={300000}
          onChange={(from, to) => { setKmFrom(from); setKmTo(to); }}
        />
      </FilterSection>

      {makeOptions.length > 0 && (
        <FilterSection title="Merke" defaultOpen={false}>
          <CheckboxGroup options={makeOptions} selected={makes} onChange={setMakes} />
        </FilterSection>
      )}

      {fuelOptions.length > 0 && (
        <FilterSection title="Drivstoff" defaultOpen={false}>
          <CheckboxGroup options={fuelOptions} selected={fuels} onChange={setFuels} />
        </FilterSection>
      )}

      {wheelDriveOptions.length > 0 && (
        <FilterSection title="Hjuldrift" defaultOpen={false}>
          <CheckboxGroup options={wheelDriveOptions} selected={wheelDrives} onChange={setWheelDrives} />
        </FilterSection>
      )}

      {transmissionOptions.length > 0 && (
        <FilterSection title="Girkasse" defaultOpen={false}>
          <CheckboxGroup options={transmissionOptions} selected={transmissions} onChange={setTransmissions} />
        </FilterSection>
      )}

      {bodyTypeOptions.length > 0 && (
        <FilterSection title="Karosseri" defaultOpen={false}>
          <CheckboxGroup options={bodyTypeOptions} selected={bodyTypes} onChange={setBodyTypes} />
        </FilterSection>
      )}
    </>
  );

  return (
    <div className="mx-auto flex max-w-screen-xl flex-col lg:flex-row gap-6 lg:gap-8 px-4 sm:px-6 py-4 sm:py-6">
      {/* Sidebar - Desktop only */}
      <aside className="hidden lg:block w-64 flex-shrink-0">
        <div className="sticky top-24 space-y-6">
          {filterContent}
        </div>
      </aside>

      {/* Filter Drawer - Mobile only */}
      <FilterDrawer
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onClear={clearFilters}
        title="Filtre"
        count={filtered.length}
      >
        {filterContent}
      </FilterDrawer>

      {/* Main */}
      {/* Main */}
      <main className="min-w-0 flex-1">
        {(auctionsLoading || pendingLoading) ? (
          <div className="grid grid-cols-1 gap-4 sm:gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-64 animate-pulse rounded-2xl bg-muted" />
            ))}
          </div>
        ) : (filtered.length === 0 && pendingAuctions.length === 0) ? (
          <div className="flex flex-col items-center">
            <EmptyState
              message={auctionsError ? "En feil oppstod" : "Ingen auksjoner matcher filteret"}
              sub={auctionsError ? auctionsError : "Prøv å endre filterene dine."}
            />
            <button
              onClick={clearFilters}
              className="mt-6 rounded-xl border-2 border-primary bg-white px-6 py-2.5 text-sm font-bold shadow-sm transition-all hover:bg-muted/30 active:scale-95 text-primary"
            >
              Nullstill alle filtre
            </button>
          </div>
        ) : (
          <div className="space-y-12">
            {/* Header row */}
            <div className="mb-6 flex flex-col lg:flex-row lg:items-end justify-between gap-4">
              <div>
                <h1
                  className="text-3xl font-black text-[#1a2e35]"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  Aktive budrunder
                </h1>
                <div className="mt-1 flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    {auctionsLoading ? "Laster..." : (
                      <>Viser <strong>{filtered.length}</strong> av <strong>{liveAuctions.length}</strong> biler</>
                    )}
                  </p>

                  {/* Mobile Filter Button */}
                  <button
                    onClick={() => setIsFilterOpen(true)}
                    className="flex items-center gap-2 rounded-xl border-2 border-primary bg-white px-4 py-2 text-sm font-bold shadow-sm transition-all active:scale-95 lg:hidden text-primary"
                  >
                    <Filter className="h-4 w-4" />
                    Filter
                  </button>
                </div>
              </div>

              {/* Sort & Mobile Filters */}
              <div className="flex items-center gap-2">
                {!auctionsLoading && liveAuctions.length > 0 && (
                  <div className="relative flex-1 sm:flex-none">
                    <button
                      onClick={() => setSortOpen((v) => !v)}
                      className="flex w-full items-center justify-between sm:justify-start gap-2 rounded-xl border bg-white px-4 py-2.5 text-sm font-semibold shadow-sm transition-all hover:border-accent active:scale-90 border-border text-primary"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">↑↓</span>
                        {currentSortLabel}
                      </div>
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    </button>
                    {sortOpen && (
                      <div
                        className="absolute right-0 top-full z-10 mt-2 w-56 overflow-hidden rounded-xl bg-white shadow-2xl border border-border animate-in fade-in zoom-in-95 duration-200"
                      >
                        {SORT_OPTIONS.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => { setSort(option.value); setSortOpen(false); }}
                            className="flex w-full items-center px-4 py-3 text-left text-sm transition-colors border-b border-muted/20 last:border-none hover:bg-muted/30"
                            style={{
                              color: sort === option.value ? "var(--accent)" : "var(--primary)",
                              background: sort === option.value ? "rgba(255,90,31,0.04)" : "transparent",
                              fontWeight: sort === option.value ? 700 : 500,
                            }}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {pendingAuctions.length > 0 && (
              <section className="space-y-6">
                <div>
                  <h2
                    className="text-2xl font-black text-[#1a2e35]"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    Avventer svar fra kunde
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Auksjoner du har vunnet som avventer selgers valg.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:gap-6">
                  {pendingAuctions.map((auction) => (
                    <BidAuctionCard
                      key={auction.id}
                      auction={auction}
                      organizationSlug={organizationSlug ?? ""}

                      isFavorite={favorites.includes(auction.id)}
                      onToggleFavorite={toggleFavorite}
                      onBidPlaced={applyAuctionEvent}
                    />
                  ))}
                </div>
              </section>
            )}

            <section className="space-y-6">
              {pendingAuctions.length > 0 && (
                <div>
                  <h2
                    className="text-2xl font-black text-[#1a2e35]"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    Aktive budrunder
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Alle pågående auksjoner på markedsplassen.
                  </p>
                </div>
              )}
              
              {filtered.length === 0 ? (
                <EmptyState
                  message="Ingen aktive auksjoner"
                  sub={
                    liveAuctions.length > 0
                      ? "Det er ingen auksjoner som matcher filtrene dine."
                      : "Det er ingen aktive budrunder akkurat nå."
                  }
                />
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:gap-6">
                  {filtered.map((auction) => (
                    <BidAuctionCard
                      key={auction.id}
                      auction={auction}
                      organizationSlug={organizationSlug ?? ""}

                      isFavorite={favorites.includes(auction.id)}
                      onToggleFavorite={toggleFavorite}
                      onBidPlaced={applyAuctionEvent}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
